import React, { useId } from 'react';
const lightBands = [{
  wavelength: '460',
  name: 'Blue LED',
  color: '#85B9FF',
  glow: '#3267DA',
  invisible: false
}, {
  wavelength: '665',
  name: 'Red laser',
  color: '#FF968D',
  glow: '#D54C45',
  invisible: false
}, {
  wavelength: '850',
  name: 'Near-infrared laser',
  color: '#CDBEFF',
  glow: '#8C70D1',
  invisible: true
}, {
  wavelength: '1064',
  name: 'Near-infrared laser',
  color: '#EAC99B',
  glow: '#B7884B',
  invisible: true
}] as const;

/** General optical patterns, not a measurement of this mask's beam or skin penetration. */
function BeamDiagram({
  spread = false
}: {
  spread?: boolean;
}) {
  const id = useId().replace(/:/g, '');
  const beam = `${id}-beam`;
  const source = `${id}-source`;
  return <svg viewBox="0 0 240 260" role="img" aria-label={spread ? 'Illustration of LED light spreading outward from a source' : 'Illustration of laser light focused into a narrow beam'} className="mx-auto block w-full max-w-[240px]" data-id="element-213" data-mp="5vv415qecuh6">
      <defs data-id="element-214" data-mp="27oatlkz3g8kp">
        <linearGradient id={beam} x1="0" y1="0" x2="0" y2="1" data-id="element-215" data-mp="3ijor5v8vmcns">
          <stop offset="0" stopColor="#E86B5E" stopOpacity=".68" data-id="element-216" data-mp="3il9k5gfjlnjj" />
          <stop offset=".85" stopColor="#C83E35" stopOpacity=".13" data-id="element-217" data-mp="3njkwbxtb8a4x" />
          <stop offset="1" stopColor="#C83E35" stopOpacity="0" data-id="element-218" data-mp="3shw668jlukn5" />
        </linearGradient>
        <linearGradient id={source} x1="0" y1="0" x2="0" y2="1" data-id="element-219" data-mp="12yjqtt13vin">
          <stop stopColor="#FFFFFF" data-id="element-220" data-mp="149lyp6tsv7n" />
          <stop offset="1" stopColor="#D7C8B2" data-id="element-221" data-mp="54sgir03sfdj" />
        </linearGradient>
      </defs>
      <path d="M24 234 H216" stroke="#C9BDAB" strokeWidth="1" data-id="element-222" data-mp="56wlzddnhmfx" />
      <path d="M120 70 V244" stroke="#C9BDAB" strokeDasharray="3 6" data-id="element-223" data-mp="a221nuggm4vk" />
      {spread ? <>
          <path d="M110 70 L20 244 H220 L130 70 Z" fill={`url(#${beam})`} data-id="element-224" data-mp="f39alwuc9a3t" />
          {[-96, -64, -32, 0, 32, 64, 96].map(offset => <path key={offset} d={`M120 70 L${120 + offset} 234`} stroke="#CC5144" strokeWidth="1.5" opacity=".4" data-id="element-225" data-mp="2gu2wt2yu0pks" />)}
          <ellipse cx="120" cy="234" rx="96" ry="7" fill="#DB6F5F" opacity=".14" data-id="element-226" data-mp="2lse69cl7sy2h" />
        </> : <>
          <path d="M107 70 H133 L136 244 H104 Z" fill={`url(#${beam})`} data-id="element-227" data-mp="2dt9gmsn7np4g" />
          {[112, 120, 128].map(x => <path key={x} d={`M${x} 70 V234`} stroke="#C34639" strokeWidth={x === 120 ? 2.5 : 1.5} opacity=".8" data-id="element-228" data-mp="28uorbo9hypus" />)}
          <ellipse cx="120" cy="234" rx="17" ry="5" fill="#C34639" opacity=".25" data-id="element-229" data-mp="2ns8pocbw1mrz" />
        </>}
      <rect x="93" y="28" width="54" height="43" rx="9" fill={`url(#${source})`} stroke="#BBAD96" data-id="element-230" data-mp="2ipzztoubb0u3" />
      <path d="M104 39 H136 M104 47 H136" stroke="#B6A58C" strokeWidth="1.5" data-id="element-231" data-mp="2wpj426k05s14" />
      <ellipse cx="120" cy="70" rx="15" ry="4" fill="#D96957" data-id="element-232" data-mp="2rrhlrnuehwqp" />
      <ellipse cx="120" cy="70" rx="9" ry="2" fill="#FFF1D9" data-id="element-233" data-mp="36gvv4vxz8e63" />
    </svg>;
}
function WavelengthMap() {
  return <figure data-wavelength-map className="relative overflow-hidden bg-[#252725] px-5 py-7 text-white md:px-8 md:py-8" data-id="element-234" data-mp="31hsr9etqgdzp">
      <figcaption className="flex flex-wrap items-baseline justify-between gap-2 border-b border-white/20 pb-5" data-id="element-235" data-mp="3fin1m30ccp10">
        <span className="font-display text-[28px] font-medium md:text-[32px]" data-id="element-236" data-mp="3bi342ybuvjec">The wavelength map</span>
        <span className="text-[11px] tracking-[.08em] text-white/70" data-id="element-237" data-mp="3u5bwfrjnee12">nm = nanometers</span>
      </figcaption>
      <div className="grid gap-7 pt-6 sm:grid-cols-2 sm:gap-0" data-id="element-238" data-mp="2w53ootdvktq">
        {[false, true].map(invisible => <div key={String(invisible)} className={invisible ? 'border-t border-white/20 pt-6 sm:border-l sm:border-t-0 sm:pl-5 sm:pt-0' : 'sm:pr-5'} data-id="element-239" data-mp="3l66yonfwuopy">
            <p className="text-[11px] font-medium uppercase tracking-[.1em] text-white/75" data-id="element-240" data-mp="3bczwfbggvou3">{invisible ? 'Near-infrared · Invisible' : 'Visible light'}</p>
            <div className="mt-5 grid grid-cols-2 gap-4" data-id="element-241" data-mp="32dcw6szh4p2m">
              {lightBands.filter(band => band.invisible === invisible).map(band => <div key={band.wavelength} data-light-band={band.wavelength} data-id="element-242" data-mp="36fzoev024c0c">
                  <div aria-hidden="true" className="relative flex h-[120px] items-center justify-center overflow-hidden md:h-[145px]" style={{
              background: `radial-gradient(ellipse at 50% 18%, ${band.glow}50, transparent 72%)`
            }} data-id="element-243" data-mp="2senf32fej8nf">
                    <span className="absolute top-4 h-[16px] w-[16px] rounded-full border-2 border-white/80" style={{
                backgroundColor: band.color,
                boxShadow: `0 0 22px 5px ${band.glow}90`
              }} data-id="element-244" data-mp="3oednvhmo7of" />
                    <span className="absolute bottom-2 top-10 w-px" style={{
                borderLeft: `2px ${band.invisible ? 'dashed' : 'solid'} ${band.color}`,
                opacity: .8
              }} data-id="element-245" data-mp="3qx1dy7y9klfy" />
                    <span className="absolute bottom-3 h-px w-9" style={{
                backgroundColor: band.color,
                opacity: .6
              }} data-id="element-246" data-mp="3h2rgwcydmyyw" />
                  </div>
                  <p className="mt-3 whitespace-nowrap font-display text-[32px] leading-none md:text-[36px]" style={{
              color: band.color
            }} data-id="element-247" data-mp="380i5lkvab1kg">{band.wavelength}<span className="ml-1 font-sans text-[11px] text-white/75" data-id="element-248" data-mp="332gqfb0zbme9">nm</span></p>
                  <p className="mt-2 text-[12px] leading-relaxed text-white/90" data-id="element-249" data-mp="169eu6tdkliyu">{band.name}</p>
                </div>)}
            </div>
          </div>)}
      </div>
      <p className="mt-6 border-t border-white/20 pt-4 text-[12px] leading-relaxed text-white/70" data-id="element-250" data-mp="18u378y9y3i4q">Violet and gold identify invisible near-infrared wavelengths in this illustration.</p>
    </figure>;
}
export function LightScience() {
  return <section id="light-science" aria-labelledby="light-science-title" className="border-t border-line bg-white" data-id="element-251" data-mp="1ik679ifhhwck">
      <div className="mx-auto max-w-page px-5 py-16 md:px-10 lg:py-24" data-id="element-252" data-mp="1nkk92iqhsa25">
        <div className="grid gap-5 lg:grid-cols-[1fr_1fr] lg:items-end lg:gap-16" data-id="element-253" data-mp="pwa17n40ys34">
          <div data-id="element-254" data-mp="uuv27or3pfsm">
            <p className="text-[13px] font-medium uppercase tracking-[.12em] text-bronze-deep" data-id="element-255" data-mp="uubut1z2u9su">A closer look at the technology</p>
            <h2 id="light-science-title" className="mt-4 font-display text-[42px] font-medium leading-[1.04] text-ink md:text-[58px]" data-id="element-256" data-mp="zv9cldg6d1tt">Laser light, explained.</h2>
          </div>
          <p className="max-w-xl text-[16px] leading-relaxed text-ink-muted" data-id="element-257" data-mp="2b7scev4d191j">164 laser emitters and 72 blue LEDs, together in one mask. See how the light patterns differ and where its four wavelengths sit in the spectrum.</p>
        </div>

        <div className="mt-10 grid items-stretch gap-6 lg:grid-cols-[.95fr_1.05fr] lg:gap-8" data-id="element-258" data-mp="b62hmgfoxgsx">
          <figure data-beam-comparison className="flex flex-col bg-canvas-deep px-5 py-7 md:px-8 md:py-8" data-id="element-259" data-mp="p5bmwl2cyqi4">
            <figcaption className="font-display text-[28px] font-medium text-ink md:text-[32px]" data-id="element-260" data-mp="k6qo8q2r8evs">Two different light patterns</figcaption>
            <div className="mt-4 grid flex-1 grid-cols-2 gap-4" data-id="element-261" data-mp="3sn3ie9fpdsl2">
              <div className="min-w-0" data-id="element-262" data-mp="3ol8klewzdxc1">
                <h3 className="text-center text-[15px] font-semibold text-ink" data-id="element-263" data-mp="3om9yrs9amzxz">Laser light</h3>
                <BeamDiagram data-id="element-264" data-mp="68agcvt5oic2" />
                <p className="mx-auto max-w-[190px] text-center text-[13px] leading-relaxed text-ink-muted" data-id="element-265" data-mp="68k9gs9hz5lz">Can be focused into a narrow beam.</p>
              </div>
              <div className="min-w-0 border-l border-line pl-4" data-id="element-266" data-mp="39u820h2isn26">
                <h3 className="text-center text-[15px] font-semibold text-ink" data-id="element-267" data-mp="34x973wb18c2x">LED light</h3>
                <BeamDiagram spread data-id="element-268" data-mp="1ke5hz4yek0k5" />
                <p className="mx-auto max-w-[190px] text-center text-[13px] leading-relaxed text-ink-muted" data-id="element-269" data-mp="1kd45qx5nh80u">Commonly spreads over a wider area.</p>
              </div>
            </div>
            <p className="mt-6 border-t border-line pt-4 text-[12px] leading-relaxed text-ink-muted" data-id="element-270" data-mp="1bf1w5t7up71y">Illustrative light patterns. Actual output depends on device optics.</p>
          </figure>
          <WavelengthMap data-id="element-271" data-mp="242eh5yyd56t5" />
        </div>

        <p className="mt-6 max-w-3xl text-[13px] leading-relaxed text-ink-muted" data-id="element-272" data-mp="240jxsp2qpmr6">Wavelength describes the light, rather than a fixed skin depth. How light interacts with skin also depends on the device’s output and individual skin characteristics.</p>
      </div>
    </section>;
}